<script setup>
import { ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  draft: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['useDraft']);

const showAlternatives = ref(false);

const handleUseDraft = text => {
  emit('useDraft', text || props.draft.text);
};

const getConfidenceColor = confidence => {
  if (confidence >= 0.8) return 'text-g-500';
  if (confidence >= 0.5) return 'text-y-500';
  return 'text-r-500';
};

const getToneIcon = tone => {
  const icons = {
    friendly: 'i-lucide-smile',
    professional: 'i-lucide-briefcase',
    empathetic: 'i-lucide-heart',
  };
  return icons[tone] || 'i-lucide-message-circle';
};
</script>

<template>
  <div class="rounded-lg bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-3">
      <h4
        class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
      >
        {{ $t('AGENTS_SIDECAR.DRAFT.TITLE') }}
      </h4>
      <div class="flex items-center gap-2">
        <span
          v-if="draft.tone"
          class="flex items-center gap-1 text-xs text-n-slate-10"
        >
          <span :class="getToneIcon(draft.tone)" />
          <span class="capitalize">{{ draft.tone }}</span>
        </span>
        <span
          v-if="draft.confidence"
          class="text-xs font-medium"
          :class="getConfidenceColor(draft.confidence)"
        >
          {{ Math.round(draft.confidence * 100) }}%
        </span>
      </div>
    </div>

    <div
      class="p-3 bg-n-solid-1 rounded-lg border border-n-weak text-n-slate-12 text-sm leading-relaxed whitespace-pre-wrap"
    >
      {{ draft.text }}
    </div>

    <div class="mt-3 flex items-center justify-between">
      <Button sm faded slate @click="handleUseDraft()">
        <span class="i-lucide-copy mr-1.5" />
        {{ $t('AGENTS_SIDECAR.DRAFT.USE_DRAFT') }}
      </Button>

      <button
        v-if="draft.alternatives?.length"
        class="text-xs text-n-slate-10 hover:text-n-slate-12 flex items-center gap-1"
        @click="showAlternatives = !showAlternatives"
      >
        <span
          :class="
            showAlternatives ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
          "
        />
        {{ $t('AGENTS_SIDECAR.DRAFT.ALTERNATIVES', draft.alternatives.length) }}
      </button>
    </div>

    <div
      v-if="showAlternatives && draft.alternatives?.length"
      class="mt-3 space-y-2"
    >
      <div
        v-for="(alt, index) in draft.alternatives"
        :key="index"
        class="p-3 bg-n-solid-3 rounded-lg border border-n-weak/50"
      >
        <p class="text-sm text-n-slate-11 leading-relaxed whitespace-pre-wrap">
          {{ alt }}
        </p>
        <button
          class="mt-2 text-xs text-n-slate-10 hover:text-n-slate-12 flex items-center gap-1"
          @click="handleUseDraft(alt)"
        >
          <span class="i-lucide-copy" />
          {{ $t('AGENTS_SIDECAR.DRAFT.USE_THIS') }}
        </button>
      </div>
    </div>
  </div>
</template>
